import {
  Container,
  Typography,
  Grid,
  Box,
  Chip,
  Button,
  TextField,
  styled,
  IconButton,
  Modal,
  Fade,
  MenuItem
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Page from 'components/Page';
import {
  BinIcon,
  BlockIcon,
  CloseIcon,
  EditIcon,
  KeyIcon,
  UnblockIcon
} from 'components/icon/DashboardIcon';
import { format } from 'date-fns';
import { Formik, Form, Field, useFormik } from 'formik';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import publicUrl from 'utils/publicUrl';
import {
  ApiCreateUser,
  ApiFindAllUsers,
  ApiBlockUsers,
  ApiUnblockUsers,
  ApiDeleteUsers,
  ApiUpdateUser,
  ApiRequestNewPassword
} from 'api';
import { debounce } from 'lodash';
import enumToText from 'utils/enumToText';
import { setFullName, setUserGroup } from 'sections/authentication/login/loginSlice';

const TableGrid = styled(DataGrid)`
  .highlight {
    color: #919eab;
  }
`;

const HeaderMenuTable = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 16px;
  padding: 16px;
  background-color: #f4f6f8;
  border-radius: 8px 8px 0px 0px;
`;

const ModalContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ModalHeader = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const ModalBody = styled('div')``;

const NoRows = styled('div')`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  line-height: 24px;
  color: #212b36;
`;

const modalCreateStyled = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'clamp(320px, 100%, 600px)',
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  padding: '32px',
  gap: '24px'
};

const modalUpdateStyled = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'clamp(320px, 100%, 600px)',
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  padding: '32px',
  gap: '24px'
};

const defaultProfile = {
  id: '',
  email: '',
  firstName: '',
  lastName: '',
  role: 'REVIEWER',
  status: 'BLOCK'
};

function CustomEmptyOverlayGrid() {
  return <NoRows>Search Not Found.</NoRows>;
}

function UserPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.userSlice);
  const userGroup = user.group[0];

  console.log('userGroup', userGroup);

  const [editUser, setEditUser] = useState(defaultProfile);
  const [openCreateUser, setOpenCreateUser] = useState(false);
  const [openUpdateUser, setOpenUpdateUser] = useState(false);
  const [listUser, setListUser] = useState([]);
  const [selectUser, setSelectUser] = useState([]);
  const [searchName, setSearchName] = useState('');
  const currentPage = useRef(0);
  const totalPages = useRef(1);
  const itemsPerPage = useRef(10);
  const totalItems = useRef(0);
  const orderBy = useRef('');

  const MySwal = withReactContent(Swal);

  const hasMeInList = useMemo(() => selectUser.includes(user.id), [selectUser, user]);

  const fetchFindAllUsers = useCallback(() => {
    const params = `?page=${currentPage.current + 1}&limit=${itemsPerPage.current}${
      orderBy.current ? `&${orderBy.current}` : ''
    }${searchName ? `&search=${searchName}` : ''}`;
    ApiFindAllUsers(params).then((response) => {
      const { data, meta } = response.data;
      currentPage.current = meta.currentPage - 1;
      totalPages.current = meta.totalPages;
      itemsPerPage.current = meta.itemsPerPage;
      totalItems.current = meta.totalItems;
      setListUser(data);
    });
  }, [searchName]);

  const formik = useFormik({
    initialValues: {
      name: '',
      surname: '',
      email: '',
      role: ''
    },
    validate: (values) => {
      const errors = {};
      if (!values.name) {
        errors.name = 'Required Name';
      }
      if (!values.surname) {
        errors.surname = 'Required Surname';
      }
      if (!values.email) {
        errors.email = 'Required Email';
      } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
        errors.email = 'Invalid email address';
      }
      if (!values.role) {
        errors.role = 'Required Role';
      }
      return errors;
    },
    onSubmit: (values) => {
      if (editUser.id) {
        ApiUpdateUser(editUser?.id ?? '', {
          firstName: values.name,
          lastName: values.surname,
          email: values.email,
          role: values.role
        })
          .then(() => {
            if (editUser.id === user.id) {
              dispatch(setFullName({ name: values.name, surname: values.surname }));
              dispatch(setUserGroup([values.role]));
            }
            closeAndClearFormCreate();
            fetchFindAllUsers();
            MySwal.fire({
              icon: 'success',
              title: 'Success',
              text: 'Account has been updated!'
            });
          })
          .catch((error) => {
            MySwal.fire({
              icon: 'error',
              title: 'Error',
              text: error.response.data.message
            });
          });
      } else {
        ApiCreateUser({
          firstName: values.name,
          lastName: values.surname,
          email: values.email,
          role: values.role
        })
          .then(() => {
            closeAndClearFormCreate();
            fetchFindAllUsers();
            MySwal.fire({
              icon: 'success',
              title: 'Success',
              text: 'Account has been created!'
            });
          })
          .catch((error) => {
            MySwal.fire({
              icon: 'error',
              title: 'Error',
              text: error.response.data.message
            });
          });
      }
    }
  });

  const closeAndClearFormCreate = useCallback(() => {
    formik.handleReset();
    setOpenCreateUser(false);
    setOpenUpdateUser(false);
    setEditUser(defaultProfile);
  }, [formik]);

  const removeSelectionRow = useCallback(
    (selectId) => {
      const filterUser = selectUser.filter((id) => id !== selectId);
      setSelectUser(filterUser);
    },
    [selectUser]
  );

  const isDisable = useMemo(() => {
    if (hasMeInList) {
      return {
        deleteColor: 'rgba(145, 158, 171, 0.8)',
        deleteDisabled: true,
        blockColor: 'rgba(145, 158, 171, 0.8)',
        blockDisabled: true,
        unblockColor: 'rgba(145, 158, 171, 0.8)',
        unblockDisabled: true,
        requestPasswordColor: '#637381',
        requestPasswordDisabled: false
      };
    }

    return {
      deleteColor: selectUser.length === 0 ? 'rgba(145, 158, 171, 0.8)' : '#637381',
      deleteDisabled: selectUser.length === 0,
      blockColor: selectUser.length === 0 ? 'rgba(145, 158, 171, 0.8)' : '#637381',
      blockDisabled: selectUser.length === 0,
      unblockColor: selectUser.length === 0 ? 'rgba(145, 158, 171, 0.8)' : '#637381',
      unblockDisabled: selectUser.length === 0,
      requestPasswordColor: selectUser.length === 0 ? 'rgba(145, 158, 171, 0.8)' : '#637381',
      requestPasswordDisabled: selectUser.length === 0
    };
  }, [selectUser.length, hasMeInList]);

  const chipColorByStatus = useCallback((status) => {
    const colorObj = { backgroundColor: '#919EAB14', color: '#637381' };
    if (`${status}`.toLowerCase() === 'block') {
      colorObj.backgroundColor = '#FF48421F';
      colorObj.color = '#FF4842';
    } else if (`${status}`.toLowerCase() === 'active') {
      colorObj.backgroundColor = '#54D62C1F';
      colorObj.color = '#229A16';
    }

    return colorObj;
  }, []);

  const chipData = useCallback(
    (params) => {
      const label = params.row?.status?.toLowerCase() ?? '';

      return {
        label,
        style: {
          ...chipColorByStatus(params.row.status),
          textTransform: 'capitalize'
        }
      };
    },
    [chipColorByStatus]
  );

  const handleDeleteUsers = useCallback(
    async (list) => {
      const result = await MySwal.fire({
        title: 'Are you sure ?',
        html: 'Do you really want to delete these ? <br>This process cannot be undone.',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        reverseButtons: true,
        confirmButtonColor: '#FF4842',
        iconHtml: `<img src="${publicUrl('/static/error.png')}">`,
        customClass: {
          icon: 'no-border'
        }
      });
      if (result.isConfirmed) {
        ApiDeleteUsers(list)
          .then(() => {
            fetchFindAllUsers();
            MySwal.fire({
              icon: 'success',
              title: 'Success',
              text: 'Delete users success!'
            });
            closeAndClearFormCreate();
          })
          .catch(() => {
            MySwal.fire('Delete users failed');
          });
      }
    },
    [closeAndClearFormCreate, fetchFindAllUsers, MySwal]
  );

  const handleBlockUsers = useCallback(
    async (list) => {
      const result = await MySwal.fire({
        title: 'Are you sure ?',
        html: 'Do you really want to block ?<br>This process will allow the user to access the system.',
        showCancelButton: true,
        confirmButtonText: 'Block',
        reverseButtons: true,
        iconHtml: `<img src="${publicUrl('/static/warning.png')}">`,
        customClass: {
          icon: 'no-border'
        }
      });
      if (result.isConfirmed) {
        ApiBlockUsers(list)
          .then(() => {
            fetchFindAllUsers();
            MySwal.fire({
              icon: 'success',
              title: 'Success',
              text: 'Block users success!'
            });
            closeAndClearFormCreate();
          })
          .catch(() => {
            MySwal.fire('Block users failed');
          });
      }
    },
    [closeAndClearFormCreate, fetchFindAllUsers, MySwal]
  );

  const handleUnblockUsers = useCallback(
    async (list) => {
      const result = await MySwal.fire({
        title: 'Are you sure ?',
        html: 'Do you really want to unblock ?<br>This process will allow the user to access the system.',
        showCancelButton: true,
        confirmButtonText: 'Unblock',
        reverseButtons: true,
        iconHtml: `<img src="${publicUrl('/static/warning.png')}">`,
        customClass: {
          icon: 'no-border'
        }
      });
      if (result.isConfirmed) {
        ApiUnblockUsers(list)
          .then(() => {
            fetchFindAllUsers();
            MySwal.fire({
              icon: 'success',
              title: 'Success',
              text: 'Unblock users success!'
            });
            closeAndClearFormCreate();
          })
          .catch(() => {
            MySwal.fire('Unblock users failed');
          });
      }
    },
    [closeAndClearFormCreate, fetchFindAllUsers, MySwal]
  );

  const handleNewPasswordUsers = useCallback(
    async (list) => {
      const result = await MySwal.fire({
        title: 'Are you sure ?',
        html: 'Do you really want to request new password ?<br>This process will allow the user to access the system.',
        showCancelButton: true,
        confirmButtonText: 'Send',
        reverseButtons: true,
        iconHtml: `<img src="${publicUrl('/static/warning.png')}">`,
        customClass: {
          icon: 'no-border'
        }
      });
      if (result.isConfirmed) {
        ApiRequestNewPassword(list)
          .then(() => {
            fetchFindAllUsers();
            MySwal.fire({
              icon: 'success',
              title: 'Success',
              text: 'Request new password success!'
            });
            closeAndClearFormCreate();
          })
          .catch((error) => {
            if (error.response) {
              console.error(error.response.data);
            }
            MySwal.fire('Request new password failed');
          });
      }
    },
    [closeAndClearFormCreate, fetchFindAllUsers, MySwal]
  );

  const rowClass = useCallback((params) => {
    const data = params.row;
    if (data?.status === 'block' || data?.status === 'pending') {
      return 'highlight';
    }

    return '';
  }, []);

  const debouncedSearch = debounce((value) => {
    setSearchName(value);
    fetchFindAllUsers(value);
  }, 500);

  useEffect(() => {
    if (!userGroup || !userGroup.includes('Master')) {
      navigate('/401', { replace: true });
    } else {
      fetchFindAllUsers();
    }

    return () => {
      currentPage.current = 0;
      totalPages.current = 1;
      itemsPerPage.current = 10;
      totalItems.current = 0;
    };
  }, [fetchFindAllUsers, navigate, userGroup]);

  return (
    <Page title="User | Aquatic monitoring system">
      <Container sx={{ marginLeft: 0, marginTop: '8px' }} maxWidth={false}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4} md={4} lg={6}>
            <Typography variant="h3" sx={{ mb: 2 }}>
              User
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8} md={8} lg={6} display="flex" justifyContent="flex-end">
            <Formik initialValues={{ query: '' }} onSubmit={() => {}}>
              {({ values, handleChange }) => (
                <Form>
                  <Field
                    name="query"
                    as={TextField}
                    label="Search name"
                    size="small"
                    sx={{ minWidth: '240px' }}
                    value={values.query}
                    onChange={(e) => {
                      handleChange(e);
                      debouncedSearch(e.target.value);
                    }}
                  />
                </Form>
              )}
            </Formik>
            <Button
              variant="contained"
              color="primary"
              sx={{ height: '40px', marginLeft: '24px' }}
              onClick={() => {
                setEditUser(defaultProfile);
                formik.setValues({
                  email: '',
                  name: '',
                  role: '',
                  surname: ''
                });
                setTimeout(() => {
                  setOpenCreateUser(true);
                }, 100);
              }}
            >
              Create New User
            </Button>
          </Grid>
          <Grid item xs={12} md={12} lg={12}>
            <Box sx={{ width: '100%', typography: 'body1' }}>
              <HeaderMenuTable>
                <Button
                  variant="string"
                  sx={{ fontWeight: '500' }}
                  startIcon={<BinIcon color={isDisable.deleteColor} />}
                  disabled={isDisable.deleteDisabled}
                  onClick={() => handleDeleteUsers(selectUser)}
                >
                  Delete
                </Button>
                <Button
                  variant="string"
                  sx={{ fontWeight: '500' }}
                  startIcon={<BlockIcon color={isDisable.blockColor} />}
                  disabled={isDisable.blockDisabled}
                  onClick={() => handleBlockUsers(selectUser)}
                >
                  Block
                </Button>
                <Button
                  variant="string"
                  sx={{ fontWeight: '500' }}
                  startIcon={<UnblockIcon color={isDisable.unblockColor} />}
                  disabled={isDisable.unblockDisabled}
                  onClick={() => handleUnblockUsers(selectUser)}
                >
                  Unblock
                </Button>
                <Button
                  variant="string"
                  sx={{ fontWeight: '500' }}
                  startIcon={<KeyIcon color={isDisable.requestPasswordColor} />}
                  disabled={isDisable.requestPasswordDisabled}
                  onClick={() => handleNewPasswordUsers(selectUser)}
                >
                  Request New Password
                </Button>
              </HeaderMenuTable>
              <TableGrid
                sx={{
                  borderColor: 'transparent',
                  '& .MuiDataGrid-virtualScroller': {
                    minHeight: '300px'
                  }
                }}
                slots={{
                  noRowsOverlay: CustomEmptyOverlayGrid
                }}
                checkboxSelection
                pagination
                cellSelection
                paginationMode="server"
                getRowClassName={rowClass}
                rowSelectionModel={selectUser}
                pageSizeOptions={[10, 15, 20]}
                paginationModel={{
                  page: currentPage.current,
                  pageSize: itemsPerPage.current
                }}
                rowCount={totalItems.current}
                rows={listUser}
                onPaginationModelChange={(model) => {
                  itemsPerPage.current = model.pageSize;
                  currentPage.current = model.page;
                  fetchFindAllUsers();
                }}
                onSortModelChange={(model) => {
                  if ([...model].length > 0) {
                    const { field = '', sort = '' } = model[0];
                    const params = `sortBy=${field}:${sort.toUpperCase()}`;
                    orderBy.current = params;
                  } else {
                    orderBy.current = '';
                  }
                  fetchFindAllUsers();
                }}
                onRowSelectionModelChange={(selection) => {
                  setSelectUser(selection);
                }}
                columns={[
                  {
                    field: 'firstName',
                    headerName: 'Name',
                    flex: 1,
                    minWidth: 192,
                    disableColumnMenu: true,
                    resizable: false
                  },
                  {
                    field: 'lastName',
                    headerName: 'Surname',
                    flex: 1,
                    minWidth: 192,
                    disableColumnMenu: true,
                    resizable: false
                  },
                  {
                    field: 'email',
                    headerName: 'Email',
                    flex: 1,
                    minWidth: 192,
                    disableColumnMenu: true,
                    resizable: false
                  },
                  {
                    field: 'role',
                    headerName: 'Role',
                    flex: 1,
                    minWidth: 140,
                    disableColumnMenu: true,
                    resizable: false,
                    renderCell: (params) => enumToText(params.row.role)
                  },
                  {
                    field: 'createdAt',
                    headerName: 'Create Date',
                    flex: 1,
                    minWidth: 120,
                    disableColumnMenu: true,
                    resizable: false,
                    renderCell: (params) => format(new Date(params.row.createdAt), 'dd/MM/yyyy')
                  },
                  {
                    field: 'status',
                    headerName: 'Status',
                    flex: 1,
                    minWidth: 100,
                    disableColumnMenu: true,
                    resizable: false,
                    renderCell: (params) => {
                      const data = chipData(params);

                      return <Chip label={data.label} sx={data.style} />;
                    }
                  },
                  {
                    field: 'action',
                    headerName: '',
                    minWidth: 60,
                    maxWidth: 60,
                    disableColumnMenu: true,
                    sortable: false,
                    resizable: false,
                    renderCell: (params) => (
                      <IconButton
                        sx={{
                          borderRadius: '8px'
                        }}
                        onClick={() => {
                          setEditUser(params.row);
                          formik.setValues({
                            email: params.row?.email ?? '',
                            name: params.row?.firstName ?? '',
                            role: params.row?.role ?? '',
                            surname: params.row?.lastName ?? ''
                          });
                          setTimeout(() => {
                            setOpenUpdateUser(true);
                            removeSelectionRow(params.row?.id ?? '');
                          }, 100);
                        }}
                      >
                        <EditIcon color="#919EAB" />
                      </IconButton>
                    )
                  }
                ]}
              />
              <Modal open={openCreateUser}>
                <Fade in={openCreateUser}>
                  <Box sx={modalCreateStyled}>
                    <ModalContainer>
                      <ModalHeader>
                        <Typography id="modal-modal-title" variant="h6" component="h2">
                          Create New User
                        </Typography>
                        <IconButton
                          sx={{
                            borderRadius: '8px'
                          }}
                          size="small"
                          onClick={closeAndClearFormCreate}
                        >
                          <CloseIcon color="#637381" />
                        </IconButton>
                      </ModalHeader>
                      <ModalBody>
                        <form onSubmit={formik.handleSubmit}>
                          <TextField
                            name="name"
                            label="Name"
                            variant="outlined"
                            fullWidth
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.name}
                            error={formik.touched.name && Boolean(formik.errors.name)}
                            helperText={formik.touched.name && formik.errors.name}
                            margin="normal"
                          />
                          <TextField
                            name="surname"
                            label="Surname"
                            variant="outlined"
                            fullWidth
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.surname}
                            error={formik.touched.surname && Boolean(formik.errors.surname)}
                            helperText={formik.touched.surname && formik.errors.surname}
                            margin="normal"
                          />
                          <TextField
                            name="role"
                            label="Role"
                            select
                            variant="outlined"
                            fullWidth
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.role}
                            error={formik.touched.role && Boolean(formik.errors.role)}
                            helperText={formik.touched.role && formik.errors.role}
                            margin="normal"
                          >
                            <MenuItem value="MASTER">Master User</MenuItem>
                            <MenuItem value="REVIEWER">Reviewer</MenuItem>
                            <MenuItem value="VIEW_ONLY">View Only</MenuItem>
                          </TextField>
                          <TextField
                            name="email"
                            label="Email"
                            type="email"
                            variant="outlined"
                            fullWidth
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.email}
                            error={formik.touched.email && Boolean(formik.errors.email)}
                            helperText={formik.touched.email && formik.errors.email}
                            margin="normal"
                          />
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              gap: '16px',
                              marginTop: '24px'
                            }}
                          >
                            <Button
                              variant="outlined"
                              color="secondary"
                              sx={{
                                borderColor: '#919EAB52'
                              }}
                              onClick={closeAndClearFormCreate}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                              variant="contained"
                              color="primary"
                              disabled={Object.keys(formik.errors).length !== 0}
                            >
                              Invite User
                            </Button>
                          </Box>
                        </form>
                      </ModalBody>
                    </ModalContainer>
                  </Box>
                </Fade>
              </Modal>
              <Modal open={openUpdateUser}>
                <Fade in={openUpdateUser}>
                  <Box sx={modalUpdateStyled}>
                    <ModalContainer>
                      <ModalHeader>
                        <Typography
                          id="modal-modal-title"
                          variant="h6"
                          component="h2"
                          sx={{
                            fontSize: '18px',
                            lineHeight: '34px'
                          }}
                        >
                          Edit User Profile
                          {editUser.status.toLowerCase() === 'block' ? (
                            <Chip
                              size="small"
                              label="The user is blocked."
                              sx={{
                                marginLeft: '12px',
                                backgroundColor: '#FF48421F',
                                color: '#FF4842'
                              }}
                            />
                          ) : null}
                        </Typography>
                        <IconButton
                          sx={{
                            borderRadius: '8px'
                          }}
                          size="small"
                          onClick={closeAndClearFormCreate}
                        >
                          <CloseIcon color="#637381" />
                        </IconButton>
                      </ModalHeader>
                      <ModalBody>
                        <Box display="flex" flexDirection="row" gap="12px">
                          <Button
                            variant="outlined"
                            sx={{ fontWeight: '500' }}
                            color="secondary"
                            startIcon={
                              <BinIcon
                                color={
                                  editUser.id === user.id ? 'rgba(145, 158, 171, 0.8)' : '#637381'
                                }
                              />
                            }
                            onClick={() => handleDeleteUsers([editUser.id])}
                            disabled={editUser.id === user.id}
                          >
                            Delete User
                          </Button>
                          {editUser.status.toLocaleLowerCase() === 'block' ? (
                            <Button
                              variant="outlined"
                              sx={{ fontWeight: '500' }}
                              startIcon={<UnblockIcon color="#2AAAE2" />}
                              onClick={() => handleUnblockUsers([editUser.id])}
                            >
                              Unblock
                            </Button>
                          ) : (
                            <Button
                              variant="outlined"
                              color="secondary"
                              sx={{ fontWeight: '500' }}
                              startIcon={
                                <BlockIcon
                                  color={
                                    editUser.id === user.id ? 'rgba(145, 158, 171, 0.8)' : '#637381'
                                  }
                                />
                              }
                              onClick={() => handleBlockUsers([editUser.id])}
                              disabled={editUser.id === user.id}
                            >
                              Block
                            </Button>
                          )}
                          <Button
                            variant="outlined"
                            color="secondary"
                            sx={{ fontWeight: '500' }}
                            startIcon={<KeyIcon color="#637381" />}
                            onClick={() => handleNewPasswordUsers([editUser.id])}
                          >
                            Request New Password
                          </Button>
                        </Box>
                        <form onSubmit={formik.handleSubmit}>
                          <TextField
                            name="name"
                            label="Name"
                            variant="outlined"
                            fullWidth
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.name}
                            error={formik.touched.name && Boolean(formik.errors.name)}
                            helperText={formik.touched.name && formik.errors.name}
                            margin="normal"
                          />
                          <TextField
                            name="surname"
                            label="Surname"
                            variant="outlined"
                            fullWidth
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.surname}
                            error={formik.touched.surname && Boolean(formik.errors.surname)}
                            helperText={formik.touched.surname && formik.errors.surname}
                            margin="normal"
                          />
                          <TextField
                            name="role"
                            label="Role"
                            select
                            variant="outlined"
                            fullWidth
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.role}
                            error={formik.touched.role && Boolean(formik.errors.role)}
                            helperText={formik.touched.role && formik.errors.role}
                            margin="normal"
                          >
                            <MenuItem value="MASTER">Master User</MenuItem>
                            <MenuItem value="REVIEWER">Reviewer</MenuItem>
                            <MenuItem value="VIEW_ONLY">View Only</MenuItem>
                          </TextField>
                          <TextField
                            name="email"
                            label="Email"
                            type="email"
                            variant="outlined"
                            fullWidth
                            value={formik.values.email}
                            margin="normal"
                            disabled
                          />
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              gap: '16px',
                              marginTop: '24px'
                            }}
                          >
                            <Button
                              variant="outlined"
                              color="secondary"
                              sx={{
                                borderColor: '#919EAB52'
                              }}
                              onClick={closeAndClearFormCreate}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                              variant="contained"
                              color="primary"
                              disabled={Object.keys(formik.errors).length !== 0}
                            >
                              Save
                            </Button>
                          </Box>
                        </form>
                      </ModalBody>
                    </ModalContainer>
                  </Box>
                </Fade>
              </Modal>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}

export default UserPage;
